import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface EventCardProps {
  event: {
    id: string;
    title: string;
    date: string;
    time: string;
    location: string;
    type: 'preparation' | 'celebration' | 'community' | 'global';
    description: string;
    organizer: string;
    attendees: number;
    image: string;
    alt: string;
    timezone: string;
  };
  onRegister: (eventId: string) => void;
  onShare: (eventId: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onRegister, onShare }) => {
  const getEventTypeColor = () => {
    switch (event.type) {
      case 'preparation':
        return 'bg-peaceful-teal text-white';
      case 'celebration':
        return 'bg-accent text-accent-foreground';
      case 'community':
        return 'bg-secondary text-secondary-foreground';
      case 'global':
        return 'bg-primary text-primary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getEventTypeLabel = () => {
    switch (event.type) {
      case 'preparation':
        return 'Preparation';
      case 'celebration':
        return 'Celebration';
      case 'community':
        return 'Community';
      case 'global':
        return 'Global Unity';
      default:
        return 'Event';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={event.image}
          alt={event.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-cta font-bold ${getEventTypeColor()}`}>
            {getEventTypeLabel()}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-headline font-bold text-foreground mb-2">
          {event.title}
        </h3>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="CalendarIcon" size={16} />
            <span>{event.date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="ClockIcon" size={16} />
            <span>{event.time} ({event.timezone})</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="MapPinIcon" size={16} />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="UserGroupIcon" size={16} />
            <span>{event.attendees} attending</span>
          </div>
        </div>

        <p className="text-sm text-foreground mb-4 line-clamp-2">
          {event.description}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground">
            By {event.organizer}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onShare(event.id)}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-200"
              aria-label="Share event"
            >
              <Icon name="ShareIcon" size={18} />
            </button>
            <button
              onClick={() => onRegister(event.id)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold hover:scale-105 transition-transform duration-300"
            >
              Register
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;